import {
    getSDKID,
    GetSDKIDResult,
    getUserInfo,
    ListChannelsType,
    ServerRegionConfigResult,
    UserInfoResult,
    validToken,
} from "@obsbot/remo-server-api";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
    getLSStore,
    GlobalStoreProperties,
    LS_Store_Name,
    LS_VERSION,
    LSStoreType,
    removeLSStore,
    setLSStore,
} from "./utils/LS-Store";
import { channelStore, getPreferencesStore } from ".";

export type UserInfo = UserInfoResult & {
    avatar: string;
    token: string;
    notSet: boolean;
    user_status: string;
};
export type ServerRegionConfig = ServerRegionConfigResult;

export type Account = {
    key: string;
    password: string;
    // if `key` type is phone, countryCode is string
    // else countryCode is null
    countryCode?: string | null;
};
export interface RTCInfo {
    uid: string;
    userSig: string;
}

export interface NeedShowGuideStruct {
    channel: boolean;
    specialEffect: boolean;
    isToJoined?: boolean;
}

export interface GlobalStoreState {
    /** 从服务其获取到的config, 暂定类型为{@link ServerRegionConfigResult} */
    serverRegionConfig: ServerRegionConfig | null;
    /** 用户信息 */
    userInfo: UserInfo | null;
    userStatus: string;
    /** 当logout时可以显示 */
    currentAccount: Account | null;
    accountHistory: Account[];
    /** RTC信息 */
    rtcInfo: RTCInfo | null;
    /** 上一次检查登录token的时间 */
    lastLoginCheck: number | null;
    /** 需要进入的房间UUID */
    toJoinRoomUUID: string;
    /** 是否需要进行频道引导 */
    needShowGuide: NeedShowGuideStruct;
    /** 更新数据 */
    updateUserInfo: (userInfo: UserInfo | null) => void;
    updateOnlineStatus: (status: string) => void;
    updateUserAvatar: (avatarURL: string) => void;
    updateUserToken: (token: string) => void;
    updateLastLoginCheck: (val: number | null) => void;
    updateServerRegionConfig: (config: ServerRegionConfigResult | null) => void;
    updateRTCInfo: (rtcInfo: RTCInfo | null) => void;
    updateToJoinRoomUUID: (roomUUID: string) => void;
    getUserInfo: (isSetImg: boolean) => Promise<UserInfoResult | null>;
    validUserToken: (token: string) => Promise<{ valid: boolean }>;
    /** 获取从服务其获取到的config */
    getRegionConfig: () => ServerRegionConfigResult | null;
    /** 调用后端获取sdkID */
    getSdkID: (userId?: string) => Promise<GetSDKIDResult>;
    destroy: () => void;
    /** 更新是否需要频道引导 */
    updateNeedShowGuide: (needShowGuide: Partial<NeedShowGuideStruct>) => void;
}

export const useGlobalStore = create<GlobalStoreState>()(
    persist(
        (set, get) => ({
            serverRegionConfig: null,
            userInfo: null,
            userStatus: "offline",
            currentAccount: null,
            accountHistory: [],
            rtcInfo: null,
            lastLoginCheck: null,
            toJoinRoomUUID: "",
            needShowGuide: {
                channel: false,
                specialEffect: false,
            },
            getUserInfo: async (isSetImg: boolean) => {
                const token = localStorage.getItem("token");
                if (!token) {
                    return null;
                }
                const userInfo = await getUserInfo();
                await channelStore.listChannels(ListChannelsType.CREATED, userInfo.user_id);
                const tempAccount = userInfo.email ?? userInfo.phone ?? "";
                get().updateUserInfo({
                    ...userInfo,
                    user_status: get().userStatus,
                    avatar: userInfo.logo,
                    account: tempAccount,
                    token,
                    notSet: isSetImg,
                });
                return userInfo;
            },
            updateUserInfo(userInfo) {
                set({ userInfo });
            },
            updateOnlineStatus(status: string) {
                const { userInfo } = get();
                set({
                    userStatus: status,
                    userInfo: { ...userInfo, user_status: status } as UserInfo,
                });
            },
            updateUserAvatar(avatarURL) {
                const { userInfo } = get();
                set({ userInfo: { ...userInfo, avatar: avatarURL } as UserInfo });
            },
            updateUserToken(token) {
                const { userInfo } = get();
                set({ userInfo: { ...userInfo, token } as UserInfo });
            },
            updateLastLoginCheck(val) {
                set({ lastLoginCheck: val });
            },
            updateServerRegionConfig(config) {
                set({ serverRegionConfig: config });
            },
            getRegionConfig() {
                return get().serverRegionConfig;
            },
            updateRTCInfo(rtcInfo) {
                set({ rtcInfo });
            },
            updateToJoinRoomUUID(roomUUID) {
                set({ toJoinRoomUUID: roomUUID });
            },
            validUserToken(token) {
                return validToken(token);
            },
            getSdkID(userId) {
                const userUUID = userId || get().userInfo!.user_id;
                return getSDKID(userUUID);
            },
            destroy() {
                set({
                    serverRegionConfig: null,
                    userStatus: "offline",
                    currentAccount: null,
                    accountHistory: [],
                    rtcInfo: null,
                    lastLoginCheck: null,
                    toJoinRoomUUID: "",
                });
            },
            updateNeedShowGuide(needShowGuide) {
                const preNeedShowGuide = get().needShowGuide;
                const userInfo = get().userInfo;
                const newShowGuide = { ...preNeedShowGuide, ...needShowGuide };
                if (
                    newShowGuide.channel === false &&
                    newShowGuide.specialEffect === false &&
                    userInfo?.user_id
                ) {
                    const preference = getPreferencesStore(userInfo.user_id);
                    preference.updateShowChannelGuide(true);
                }
                set({ needShowGuide: newShowGuide });
            },
        }),
        {
            name: "Remo-Global-Store",
            version: LS_VERSION,
            partialize: state => ({
                serverRegionConfig: state.serverRegionConfig,
                rtcInfo: state.rtcInfo,
                userInfo: state.userInfo,
            }),
        },
    ),
);

export const useLSGlobalStore = (): LSStoreType<GlobalStoreProperties> | null =>
    getLSStore(LS_Store_Name.Global);

export const setLSGlobalStore = (val: LSStoreType<GlobalStoreProperties>): void =>
    setLSStore(LS_Store_Name.Global, val);

export const removeLSGlobalStore = (): void => removeLSStore(LS_Store_Name.Global);

export const globalStore = useGlobalStore.getState();
